import React, { useEffect, useState } from "react";
import instance from "../apiInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBagShopping, faFilter, faClock, faPlus } from "@fortawesome/free-solid-svg-icons";
import "./FavoritesManagement.css";
import "./CartRadarManagement.css";

const money = (v) => {
  const n = Number(v);
  if (!n) return "a confirmar";
  return `R$ ${n.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
};

const itemName = (item) =>
  [item?.brand, item?.title || item?.name].filter(Boolean).join(" ").trim() || "Peça";

const personLabel = (row) =>
  (row?.name && String(row.name).trim()) || row?.email || "Cliente";

function piecePhoto(item) {
  const raw = String(item?.photo || item?.image_url || item?.url || item?.productId || "").trim();
  if (!/^https?:\/\//i.test(raw)) return "";
  try {
    const parsed = new URL(raw);
    parsed.pathname = parsed.pathname
      .split("/")
      .map((seg) => {
        try {
          return encodeURIComponent(decodeURIComponent(seg));
        } catch {
          return encodeURIComponent(seg);
        }
      })
      .join("/");
    return parsed.toString();
  } catch {
    return raw;
  }
}

const thumbStyle = {
  width: 120,
  height: 120,
  objectFit: "cover",
  borderRadius: 8,
  background: "#f4f4f4",
  display: "block",
};

const CartRadarManagement = () => {
  const [day, setDay] = useState("");
  const [days, setDays] = useState([]);
  const [orders, setOrders] = useState([]);
  const [adds, setAdds] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState("adds");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (nextDay, { silent } = {}) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const params = {};
      if (nextDay) params.day = nextDay;
      const res = await instance.get("/admin/pwa-cart/radar", { params });
      setOrders(res.data?.orders || []);
      setAdds(res.data?.adds || []);
      setLogs(res.data?.logs || []);
      setDays(res.data?.days || []);
    } catch {
      setError("Não carregou os carrinhos. Confere se a tabela user_events existe.");
      setOrders([]);
      setAdds([]);
      setLogs([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    void load(day);
  }, [day]);

  useEffect(() => {
    const id = setInterval(() => {
      void load(day, { silent: true });
    }, 20000);
    return () => clearInterval(id);
  }, [day]);

  const counts = {
    adds: `${adds.length} cliente${adds.length === 1 ? "" : "s"}`,
    orders: `${orders.length} pedido${orders.length === 1 ? "" : "s"}`,
    logs: `${logs.length} log${logs.length === 1 ? "" : "s"}`,
  };

  const renderPieceGrid = (items) => (
    <div className="cart-piece-grid">
      {(items || []).map((item, i) => {
        const src = piecePhoto(item);
        return (
          <div key={item.key || `${item.title || item.name}-${i}`} className="cart-piece">
            {src ? (
              <img
                src={src}
                alt={itemName(item)}
                style={thumbStyle}
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="cart-piece-empty" style={thumbStyle} />
            )}
            <div className="cart-piece-name">
              {item.qty ? `${item.qty}× ` : ""}
              {itemName(item)}
            </div>
            <div className="cart-piece-meta">{money(item.price ?? item.line_brl)}</div>
            {item.at_label ? <div className="cart-piece-meta">{item.at_label}</div> : null}
          </div>
        );
      })}
    </div>
  );

  const renderAdds = () => {
    if (!adds.length) {
      return (
        <div className="fav-loading">
          Ninguém adicionou ainda. Quando ela clicar em Adicionar, aparece aqui com a foto.
        </div>
      );
    }
    return adds.map((row) => (
      <div key={row.user_id || row.email} className="cart-card">
        <div className="cart-card-head">
          <div>
            <strong>{personLabel(row)}</strong>
            <div className="cart-card-sub">
              {row.email && row.name ? `${row.email} · ` : ""}
              {row.last_at_label || ""}
              {row.adds_count ? ` · ${row.adds_count} peça${row.adds_count === 1 ? "" : "s"}` : ""}
            </div>
          </div>
          <div className="cart-card-status">adicionou</div>
        </div>
        {renderPieceGrid(row.items)}
      </div>
    ));
  };

  const renderOrders = () => {
    if (!orders.length) {
      return (
        <div className="fav-loading">
          Nenhum carrinho fechado ainda. Quando ela clicar em Fechar pedido, aparece aqui.
        </div>
      );
    }
    return orders.map((order) => (
      <div key={order.order_id} className="cart-card">
        <div className="cart-card-head">
          <div>
            <strong>{personLabel(order)}</strong>
            <div className="cart-card-sub">
              {order.email && order.name ? `${order.email} · ` : ""}
              {order.order_id} · {order.closed_on}
              {order.created_at
                ? ` · ${new Date(order.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`
                : ""}
            </div>
          </div>
          <div className="cart-card-status">{money(order.total_brl)}</div>
        </div>
        <div className="cart-card-flags">
          Fotos no bot: {order.photos_sent ? "sim" : "não"} · Canal de vendas: {order.sales_sent ? "sim" : "não"}
        </div>
        {renderPieceGrid(order.items)}
      </div>
    ));
  };

  const renderLogs = () => {
    if (!logs.length) {
      return (
        <div className="fav-loading">
          Sem log ainda. Cada Adicionar e cada Fechar pedido entra nesta lista.
        </div>
      );
    }
    return (
      <div className="cart-log">
        {logs.map((log) => {
          const src = piecePhoto(log);
          return (
          <div key={log.id} className="cart-log-row">
            {src ? (
              <img
                src={src}
                alt=""
                className="cart-log-thumb"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="cart-log-thumb is-empty" />
            )}
            <div className="cart-log-body">
              <div className="cart-log-top">
                <strong>{personLabel(log)}</strong>
                <span className={`cart-log-pill ${log.type === "pwa_cart_close" ? "is-close" : "is-add"}`}>
                  {log.label}
                </span>
              </div>
              <div className="cart-log-meta">
                {log.at_label}
                {log.email && log.name ? ` · ${log.email}` : ""}
                {log.title ? ` · ${log.title}` : ""}
                {log.type === "pwa_cart_close" && log.items_count
                  ? ` · ${log.items_count} peça${log.items_count === 1 ? "" : "s"}`
                  : ""}
                {log.price ? ` · ${money(log.price)}` : ""}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fav-root">
      <div className="fav-header">
        <div>
          <h2>
            <FontAwesomeIcon icon={faBagShopping} style={{ color: "#B8962E", marginRight: 8 }} />
            Carrinhos
          </h2>
          <p>
            Quem adicionou, as peças que ela salvou, pedidos fechados no Mini App e o log de cada toque.
          </p>
        </div>
      </div>

      <div className="fav-header-actions" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={`fav-view-btn ${tab === "adds" ? "active" : ""}`}
          onClick={() => setTab("adds")}
        >
          <FontAwesomeIcon icon={faPlus} /> Adicionaram
        </button>
        <button
          type="button"
          className={`fav-view-btn ${tab === "orders" ? "active" : ""}`}
          onClick={() => setTab("orders")}
        >
          <FontAwesomeIcon icon={faBagShopping} /> Pedidos fechados
        </button>
        <button
          type="button"
          className={`fav-view-btn ${tab === "logs" ? "active" : ""}`}
          onClick={() => setTab("logs")}
        >
          <FontAwesomeIcon icon={faClock} /> Logs
        </button>
      </div>

      <div className="fav-filters">
        <FontAwesomeIcon icon={faFilter} style={{ color: "#888" }} />
        <select
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="fav-select"
        >
          <option value="">Todos os dias</option>
          {days.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <span className="fav-total">{counts[tab]}</span>
      </div>

      {loading ? (
        <div className="fav-loading">Carregando...</div>
      ) : error ? (
        <div className="fav-loading">{error}</div>
      ) : tab === "adds" ? (
        renderAdds()
      ) : tab === "orders" ? (
        renderOrders()
      ) : (
        renderLogs()
      )}
    </div>
  );
};

export default CartRadarManagement;
